import { Badge, Button, Flex, Paper, Table } from '@mantine/core'
import { modals } from '@mantine/modals'
import PolicyCheckHOC from '@v2/commons/HOC/policyCheckHOC'
import FilterChevrons from '@v2/commons/components/FilterTableOrderBy/FilterTableOrderBy.component'
import { OrderByType } from '@v2/commons/interface/enums'
import { formatDate } from '@v2/commons/utils/time'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'

import { Icons } from 'src/common/components/ui'

import {
  EscalationIssueEnum,
  IEscalation,
  NON_BENEFIT_OPTIONS,
} from '../../escalation.types'
import { openUpdateEscalationModal } from '../../views/UpdateEscalationModal.view'
import DeleteEscalationContent from '../EscalationModals/deleteEscalationModal.component'
import { MantineProviderWrapper } from './MantineBadgeColorResolver'
import styles from './escalation-table.module.scss'

interface EscalationTableProps {
  escalations: IEscalation[]
  handleOrderBy: (field: string, orderBy: OrderByType) => void
  filterOrderBy: { field: string; orderBy: OrderByType }
}

const EscalationTable = ({
  escalations,
  handleOrderBy,
  filterOrderBy,
}: EscalationTableProps) => {
  const handleOpenUpdateEscalationModal = (id: string, personId: string) => {
    openUpdateEscalationModal({
      selectedItem: id,
      personId,
    })
  }
  const getEscalaitonStatusClassName = (status: string) => {
    const splitted = status.split(' ')
    return `${splitted[splitted.length - 1]}-status`
  }

  const handleDelete = (escalationId: string) => {
    modals.open({
      centered: true,
      withCloseButton: true,
      size: 'md',
      children: (
        <DeleteEscalationContent
          onMutationSuccess={() => modals.closeAll()}
          escalationId={escalationId}
        />
      ),
    })
  }

  const getNonBenefitLabelByValue = (value: string) => {
    const option = NON_BENEFIT_OPTIONS.find((option) => option.value === value)
    return option ? option.label : value
  }

  return (
    <MantineProviderWrapper>
      <Table
        layout="fixed"
        classNames={{
          table: styles.table,
          thead: styles['table-head'],
          th: styles['table-th'],
          td: styles['table-td'],
        }}
        highlightOnHover={true}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Flex justify="center" align="center" gap="md">
                Name
                <FilterChevrons
                  field="person.lastName"
                  filterOrderBy={filterOrderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Flex>
            </Table.Th>
            <Table.Th>
              <Flex justify="center" align="center" gap="md">
                Issue
                <FilterChevrons
                  field="escalation.issue"
                  filterOrderBy={filterOrderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Flex>
            </Table.Th>
            <Table.Th>
              <Flex justify="center" align="center" gap="md">
                Benefit / Service
                <FilterChevrons
                  field="escalation.benefit"
                  filterOrderBy={filterOrderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Flex>
            </Table.Th>
            <Table.Th>
              <Flex justify="center" align="center" gap="md">
                Priority
                <FilterChevrons
                  field="task.priority"
                  filterOrderBy={filterOrderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Flex>
            </Table.Th>
            <Table.Th>
              <Flex justify="center" align="center" gap="md">
                Status
                <FilterChevrons
                  field="escalation.formStatus"
                  filterOrderBy={filterOrderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Flex>
            </Table.Th>
            <Table.Th>
              <Flex justify="center" align="center" gap="md">
                Assigned To
                <FilterChevrons
                  field="assignedTo.username"
                  filterOrderBy={filterOrderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Flex>
            </Table.Th>
            <Table.Th>
              <Flex justify="center" align="center" gap="md">
                Created Date
                <FilterChevrons
                  field="task.createdAt"
                  filterOrderBy={filterOrderBy}
                  handleOrderBy={handleOrderBy}
                />
              </Flex>
            </Table.Th>
            <PolicyCheckHOC
              policyName={WbfPolicies.is_delete_escalation_by_id_enabled}
            >
              <Table.Th style={{ width: 40 }}></Table.Th>
            </PolicyCheckHOC>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {escalations?.map((escalation) => (
            <Table.Tr
              key={escalation.id}
              onClick={() =>
                handleOpenUpdateEscalationModal(
                  escalation.id,
                  escalation.person.id,
                )
              }
            >
              <Table.Td>
                {escalation.person.firstName +
                  ' ' +
                  escalation.person.firstName}
              </Table.Td>
              <Table.Td tt="capitalize">
                {escalation.issue === EscalationIssueEnum.BENEFIT
                  ? 'benefit'
                  : 'non benefit'}
              </Table.Td>
              <Table.Td tt="capitalize">
                {escalation.issue === EscalationIssueEnum.BENEFIT
                  ? escalation?.benefit?.name
                  : getNonBenefitLabelByValue(escalation.issue)}
              </Table.Td>
              <Table.Td>
                <Badge
                  variant={`${escalation.priority.toLowerCase()}-priority`}
                  radius="sm"
                  className={styles['colored-badge']}
                  leftSection={<Icons.BonfirePriority />}
                >
                  {escalation.priority}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Badge
                  variant={getEscalaitonStatusClassName(escalation.formStatus)}
                  radius="sm"
                  className={styles['colored-badge']}
                  leftSection={<Paper className={styles.circle} />}
                >
                  {escalation.formStatus}
                </Badge>
              </Table.Td>
              <Table.Td>
                {escalation.assignedTo.firstName +
                  ' ' +
                  escalation.assignedTo.lastName}
              </Table.Td>
              <Table.Td>
                {formatDate(escalation.createdAt, 'MM/DD/YYYY')}
              </Table.Td>
              <PolicyCheckHOC
                policyName={WbfPolicies.is_delete_escalation_by_id_enabled}
              >
                <Table.Td>
                  <Button
                    className={styles['delete-button']}
                    variant="transparent"
                    ta="center"
                    p={0}
                    leftSection={<Icons.Trash height={'20px'} />}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(escalation.id)
                    }}
                  />
                </Table.Td>
              </PolicyCheckHOC>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </MantineProviderWrapper>
  )
}

export default EscalationTable
